import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Search, Filter, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { products, getWhatsAppLink, WHATSAPP_DEFAULT } from '../mock';

const Products = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');

  const categories = ['All', 'Jaggery Products', 'Dehydrated Products', 'Rice & Grains', 'Fresh Produce'];

  const filteredProducts = products.filter((product) => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.shortDescription.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || product.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="min-h-screen bg-white pt-20">
      <Helmet>
        <title>Export Products – Jaggery, Basmati Rice, Dehydrated Products | Agro Corp India</title>
        <meta name="description" content="Browse our complete catalog of export-quality agricultural products: cane jaggery, basmati rice, onion powder, moringa, curry leaves, banana powder. Worldwide wholesale bulk supply from India." />
        <meta name="keywords" content="agricultural products export India, wholesale food products, bulk jaggery supplier, basmati rice exporter, dehydrated products wholesale, B2B agro products worldwide" />
        <link rel="canonical" href="https://www.agrocorpindia.com/products" />
      </Helmet>

      {/* Hero Section */}
      <section
        className="relative h-[50vh] flex items-center justify-center bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url('https://images.unsplash.com/photo-1720456764346-1abff7d43efe')`,
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center z-10">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Our Products
          </h1>
          <p className="text-xl text-green-100 max-w-3xl mx-auto">
            Premium quality agricultural products for worldwide wholesale export
          </p>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-12 bg-gradient-to-b from-green-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row gap-6 items-center justify-between">
            <div className="relative w-full md:w-96">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                type="text"
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                data-testid="product-search-input"
                className="pl-10 pr-4 py-3 w-full border-2 border-gray-200 focus:border-green-500 rounded-lg"
              />
            </div>
            <div className="flex items-center gap-2 w-full md:w-auto overflow-x-auto pb-2 md:pb-0">
              <Filter className="text-gray-600 w-5 h-5 flex-shrink-0" />
              {categories.map((category) => (
                <Button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  data-testid={`filter-${category.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`whitespace-nowrap transition-all duration-300 ${
                    selectedCategory === category
                      ? 'bg-green-700 hover:bg-green-800 text-white'
                      : 'bg-white hover:bg-green-50 text-gray-700 border-2 border-gray-200'
                  }`}
                >
                  {category}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {filteredProducts.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredProducts.map((product) => (
                <Card
                  key={product.id}
                  data-testid={`product-card-${product.id}`}
                  className="group overflow-hidden hover:shadow-2xl transition-all duration-300 border-2 border-transparent hover:border-green-200"
                >
                  <div className="relative overflow-hidden">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-72 object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute top-4 right-4 bg-amber-500 text-white px-3 py-1 rounded-full text-xs font-semibold">
                      {product.category}
                    </div>
                  </div>
                  <CardContent className="p-6">
                    <h3 className="text-2xl font-bold text-green-900 mb-2">{product.name}</h3>
                    <p className="text-gray-600 mb-2 text-sm">{product.shortDescription}</p>
                    {product.hsnCode && (
                      <p className="text-xs text-gray-500 mb-4">HSN: {product.hsnCode}</p>
                    )}

                    <div className="flex flex-col gap-2">
                      <Link to={`/products/${product.id}`}>
                        <Button data-testid={`inquire-btn-${product.id}`} className="w-full bg-green-700 hover:bg-green-800 text-white transition-all duration-300">
                          Inquire Now
                          <ArrowRight className="ml-2 w-4 h-4" />
                        </Button>
                      </Link>
                      <a href={getWhatsAppLink(product.name)} target="_blank" rel="noopener noreferrer" className="w-full">
                        <Button data-testid={`whatsapp-btn-${product.id}`} variant="outline" className="w-full border-2 border-green-600 text-green-700 hover:bg-green-50 transition-all duration-300">
                          <MessageCircle className="mr-2 w-4 h-4" />
                          Inquire on WhatsApp
                        </Button>
                      </a>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <p className="text-2xl text-gray-600 mb-4">No products found</p>
              <p className="text-gray-500">Try adjusting your search or filter criteria</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-green-700 to-green-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Need a Custom Bulk Order?
          </h2>
          <p className="text-xl text-green-100 mb-8 max-w-2xl mx-auto">
            Contact us with your specific requirements and we'll prepare a customized export quote
          </p>
          <a href={WHATSAPP_DEFAULT} target="_blank" rel="noopener noreferrer">
            <Button data-testid="products-cta-whatsapp-btn" className="bg-amber-500 hover:bg-amber-600 text-white px-8 py-4 text-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105">
              <MessageCircle className="mr-2 w-5 h-5" />
              Inquire on WhatsApp
            </Button>
          </a>
        </div>
      </section>
    </div>
  );
};

export default Products;
